import React, { useMemo } from "react";
import { ChangedTextActivityCard } from "./ChangedTextActivityCard";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  getDocumentFetchStatusByUrn,
  getDocumentsByUrn,
} from "@adsk/acc-issues-core/state/redux/ducks/documents";
import { ActivityText } from "@adsk/alloy-react-activity-log";
import { activityTextDefaultStyle } from "./activityTextDefaultStyle";
import { FetchStatus } from "@adsk/acc-issues-core/state/redux/fetchHelpers";

type ChangedLinkedDocumentPropTypes = {
  commonActivityProps: any;
  after: string;
  before: string;
};

enum LinkedDocumentsChangeType {
  AddedToFile = "addedToFile",
  RepositionedOnFile = "repositionedOnFile",
  RemovedFromFile = "removedFromFile",
  Unknown = "unknown",
}

export const ChangedLinkedDocument = ({
  commonActivityProps,
  after,
  before,
}: ChangedLinkedDocumentPropTypes) => {
  const fileUrn = after ? after : before;
  const { T } = useTranslations();

  const documentsByUrn = useSelector(getDocumentsByUrn);
  const selectDocumentFetchStatusByUrn = useSelector(
    getDocumentFetchStatusByUrn
  );

  const file = documentsByUrn[fileUrn];
  const documentFetchStatus =
    selectDocumentFetchStatusByUrn(fileUrn) ?? FetchStatus.Initial;
  let changeType;

  if (after && !before) {
    changeType = LinkedDocumentsChangeType.AddedToFile;
  } else if (after && before) {
    changeType = LinkedDocumentsChangeType.RepositionedOnFile;
  } else if (!after && before) {
    changeType = LinkedDocumentsChangeType.RemovedFromFile;
  } else {
    changeType = LinkedDocumentsChangeType.Unknown;
  }

  const fileNamePlaceholder = T.translate("activityLog.fileWasDeleted");
  const fileName = useMemo(() => {
    if (!fileUrn) {
      return fileNamePlaceholder;
    }
    if (documentFetchStatus === FetchStatus.Error) {
      return T.translate("activityLog.couldNotDisplay");
    }
    if (file?.name) {
      return file.name;
    }
    if (documentFetchStatus === FetchStatus.Success) {
      return fileNamePlaceholder;
    }
    return fileNamePlaceholder;
  }, [T, documentFetchStatus, file?.name, fileNamePlaceholder, fileUrn]);

  const { isAggregated } = commonActivityProps;
  if (isAggregated) {
    return <ActivityText style={activityTextDefaultStyle} text={fileName} />;
  }

  let title: string;
  switch (changeType) {
    case LinkedDocumentsChangeType.AddedToFile:
      title = T.translate(
        "activityLog.titles.issuePlacement.document.added"
      ) as string;
      break;
    case LinkedDocumentsChangeType.RepositionedOnFile:
      title = T.translate(
        "activityLog.titles.issuePlacement.document.repositioned"
      ) as string;
      break;
    case LinkedDocumentsChangeType.RemovedFromFile:
      title = T.translate(
        "activityLog.titles.issuePlacement.document.removed"
      ) as string;
      break;
    default:
      title = "";
  }

  return (
    <ChangedTextActivityCard
      {...commonActivityProps}
      title={title}
      text={fileName}
    />
  );
};
